import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Folder, Tag } from 'lucide-react';
import type { AnalysisResult } from '@/lib/shelfMappingUtils';

interface ImportAnalysisSummaryProps {
  analysis: AnalysisResult;
}

export function ImportAnalysisSummary({ analysis }: ImportAnalysisSummaryProps) {
  const totalCustomShelves = analysis.customShelves.length;
  const hasCustomShelves = totalCustomShelves > 0;

  return (
    <Card className="bg-muted/30">
      <CardContent className="p-4 space-y-4">
        {/* Stats */}
        <div className="flex flex-wrap gap-4 justify-center text-center">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <div>
              <span className="text-2xl font-bold">{analysis.totalBooks}</span>
              <span className="text-muted-foreground ml-1">books</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-secondary" />
            <div>
              <span className="text-2xl font-bold">{analysis.exclusiveShelves.length}</span>
              <span className="text-muted-foreground ml-1">shelves</span>
            </div>
          </div>

          {hasCustomShelves && (
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-muted-foreground" />
              <div>
                <span className="text-2xl font-bold">{totalCustomShelves}</span>
                <span className="text-muted-foreground ml-1">custom tags</span>
              </div>
            </div>
          )}
        </div>

        {/* Exclusive Shelf Breakdown */}
        <div className="flex flex-wrap gap-2 justify-center">
          {analysis.exclusiveShelves.map((shelf) => (
            <Badge key={shelf.name} variant="secondary" className="gap-1">
              <span className="font-medium">{shelf.name}</span>
              <span className="text-muted-foreground">({shelf.bookCount})</span>
            </Badge>
          ))}
        </div>

        {/* Sample books */}
        {analysis.sampleBooks.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Sample books detected:</p>
            <div className="space-y-1">
              {analysis.sampleBooks.slice(0, 3).map((book, i) => (
                <div key={i} className="text-sm flex items-center gap-2">
                  <span className="font-medium truncate max-w-[200px]">{book.title}</span>
                  <span className="text-muted-foreground">by {book.author}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
